import { useQuery, UseQueryProps } from '@/hooks/use-query';
import { useMutation, UseMutationProps } from '@/hooks/use-mutation';
import { WidgetSdk } from '@/sdk';
import { useWidgetContext } from './use-widget-context';

const sdkCacheKey = (sdk: WidgetSdk) => (key: string) =>
  `${sdk.client.authToken || ''}-${key}`;

type UseSdkRequestHandler<TReturn, TArgs = undefined> = (
  sdk: WidgetSdk,
  args: TArgs
) => Promise<TReturn>;

export function useSdkQuery<T>(
  key: string | null,
  handler: UseSdkRequestHandler<T>,
  props?: UseQueryProps<T>
) {
  const { sdk } = useWidgetContext();

  const cacheKey = sdkCacheKey(sdk);

  return useQuery(key && cacheKey(key), () => handler(sdk, undefined), props);
}

export function useSdkMutation<T, TArg, TError extends Error = Error>(
  key: string | null,
  handler: UseSdkRequestHandler<T, TArg>,
  props?: UseMutationProps<T, TError>
) {
  const { sdk } = useWidgetContext();

  const cacheKey = sdkCacheKey(sdk);

  return useMutation(
    key && cacheKey(key),
    (args: TArg) => handler(sdk, args),
    props
  );
}
